import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import WorkbookFloatingCta from "@/components/WorkbookFloatingCta";

const AboutSection = lazy(() => import("@/components/AboutSection"));
const ServicesSection = lazy(() => import("@/components/ServicesSection"));
const PrimebaseSection = lazy(() => import("@/components/PrimebaseSection"));
const SecnosticSection = lazy(() => import("@/components/SecnosticSection"));
const SpeakerSection = lazy(() => import("@/components/SpeakerSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));

const faqs = [
  { q: "Was ist ein Fachsprechpartner?", a: "Ein Fachsprechpartner ist ein fachlich versierter Ansprechpartner, der zwischen IT, OT, Gebäudetechnik und Security übersetzt und Bauherren, Betreiber sowie Fachplaner berät. Tim Bauer tritt unter dieser Marke als unabhängiger Berater auf." },
  { q: "Was macht ein GA-Fachplaner?", a: "Ein GA-Fachplaner plant die Gebäudeautomation und sorgt dafür, dass IT, OT und Security sauber zusammenspielen. Fachsprechpartner unterstützt GA-Fachplaner bei OT-Security, BACnet/SC und Lastenheften." },
  { q: "Welche Standards sind für OT-Security in Smart Buildings relevant?", a: "Relevante Standards sind IEC 62443 für OT-Security, NIS2 als EU-Cybersecurity-Richtlinie, KRITIS für kritische Infrastrukturen sowie BACnet und BACnet/SC für sichere Kommunikation in der Gebäudeautomation." },
  { q: "Wie kann ich Tim Bauer für einen Vortrag oder ein Sparring buchen?", a: "Über das Kontaktformular auf fachsprechpartner.de oder per E-Mail an info@fachsprechpartner.de. Tim Bauer hält Vorträge und Keynotes zu OT-Security, Smart Buildings und IT/OT-Konvergenz." },
  { q: "Gilt NIS2 für Krankenhäuser und ihre Gebäudeautomation?", a: "Ja. Krankenhäuser ab 100 Betten zählen in Deutschland zu KRITIS und fallen damit unter NIS2. Die Gebäudeautomation (HLK, Zutritt, USV, Medizingase) gehört zu den abzusichernden OT-Systemen. Pflicht sind Risikoanalyse, Asset-Inventar, Patch- und Incident-Prozesse sowie technische Härtung nach IEC 62443. Fachsprechpartner unterstützt Kliniken bei der NIS2-Readiness." },
  { q: "Wie funktioniert eine BACnet/SC-Migration in Bestandsgebäuden?", a: "Eine BACnet/SC-Migration läuft typischerweise in vier Schritten: Asset- und Topologie-Aufnahme, Definition des Hub- und Zertifikats-Konzepts (PKI), schrittweise Umstellung der Controller über BACnet/SC-Router parallel zum Bestand, Außerbetriebnahme der unverschlüsselten BACnet/IP-Strecken. Wichtig sind Lifecycle der Zertifikate und kompatible Firmwarestände der Feldgeräte. Fachsprechpartner begleitet GA-Fachplaner und Betreiber durch alle Phasen." },
  { q: "Was kostet ein OT-Security-Audit für ein Smart Building?", a: "Ein OT-Security-Audit für ein Smart Building startet je nach Umfang typischerweise zwischen 5.000 und 25.000 Euro. Preistreiber sind Anzahl der Liegenschaften, Tiefe der technischen Analyse (passiv, aktiv, Pentest), Anzahl der GA-Gewerke und der Compliance-Rahmen (IEC 62443, NIS2, KRITIS). Fachsprechpartner erstellt nach einem kostenlosen Erstgespräch ein verbindliches Festpreisangebot." },
];

const services = [
  { id: "service-ot-security", name: "OT-Security Beratung für Gebäudeautomation", serviceType: "OT-Security Consulting", description: "Unabhängige Beratung zu OT-Security in Smart Buildings: Bedrohungsanalyse, IEC 62443, NIS2-Vorbereitung, BACnet/SC-Migration und Härtung von Gebäudeautomations-Netzen.", audience: "Gebäudebetreiber, Facility Manager, GA-Fachplaner, KRITIS-Betreiber" },
  { id: "service-ga-fachplanung", name: "GA-Fachplanung und Lastenheftunterstützung", serviceType: "Gebäudeautomation Fachplanung", description: "Unterstützung von GA-Fachplanern bei Lastenheft, Ausschreibung und Abnahme. Schwerpunkt auf IT/OT-Konvergenz, sicherer Netzarchitektur und herstellerneutraler Spezifikation.", audience: "GA-Fachplaner, Bauherren, Generalunternehmer" },
];

const podcastEpisodes = [
  {
    name: "OT-Security in der Gebäudeautomation: Expert Talk mit Tim Bauer (YouTube)",
    url: "https://www.youtube.com/watch?v=8OGBuOqRq34",
    series: "EXPERT TALK",
    image: "https://fachsprechpartner.de/podcast-expert-talk-ot-security.jpg",
    videoUrl: "https://www.youtube.com/watch?v=8OGBuOqRq34",
    embedUrl: "https://www.youtube.com/embed/8OGBuOqRq34",
    uploadDate: "2026-10-07T23:44:10-07:00",
    description: "Expert Talk mit ondeso über OT-Security in der Gebäudeautomation: warum Gebäudeautomation echte OT ist, wie stark vernetzte Gebäude mit Heizung, Lüftung, Zutritt, Beleuchtung und Brandmeldetechnik auf IT-Systeme zurückgreifen und wie Betreiber sie souverän absichern.",
  },
  {
    name: "RADIO OT mit Tim Bauer (YouTube)",
    url: "https://www.youtube.com/watch?v=88wgcJg9S6k",
    series: "RADIO OT",
    image: "https://img.youtube.com/vi/88wgcJg9S6k/maxresdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=88wgcJg9S6k",
    embedUrl: "https://www.youtube.com/embed/88wgcJg9S6k",
    uploadDate: "2025-03-18T09:00:00+01:00",
    description: "Tim Bauer spricht bei RADIO OT über OT-Security in der Gebäudeautomation, IEC 62443 und die Zusammenarbeit von IT, OT und Fachplanung.",
  },
  {
    name: "RADIO OT mit Tim Bauer (Spotify)",
    url: "https://open.spotify.com/episode/285KC5fCi9epUHFNsT8btR",
    series: "RADIO OT",
    image: "https://fachsprechpartner.de/podcast-radio-ot-spotify.png",
    audioUrl: "https://open.spotify.com/episode/285KC5fCi9epUHFNsT8btR",
    description: "RADIO OT Episode mit Tim Bauer zu OT-Security, Smart Buildings und der Rolle des Fachsprechpartners zwischen IT und Gebäudetechnik.",
  },
  {
    name: "OT SECURITY MADE SIMPLE mit Tim Bauer",
    url: "https://open.spotify.com/episode/0oS20cfp6loGrBmMYt2dpA",
    series: "OT SECURITY MADE SIMPLE",
    image: "https://fachsprechpartner.de/podcast-ot-security-made-simple.png",
    audioUrl: "https://open.spotify.com/episode/0oS20cfp6loGrBmMYt2dpA",
    description: "IEC 62443-basierte OT-Security als Betriebsmodell für Betreiber von Gebäudeautomation und OT-Organisationen.",
  },
  {
    name: "BACnet Secure Connect: Der Heilsbringer für smarte Gebäude im Realitätscheck",
    url: "https://open.spotify.com/episode/1nRZv2EUvlpY1yByR3cLyX",
    series: "OT SECURITY MADE SIMPLE",
    image: "https://fachsprechpartner.de/podcast-bacnet-sc-ot-security-made-simple.jpg",
    audioUrl: "https://open.spotify.com/episode/1nRZv2EUvlpY1yByR3cLyX",
    description: "BACnet/SC im Realitätscheck: Was die sichere Kommunikation in der Gebäudeautomation wirklich leistet und worauf Betreiber bei der Migration achten müssen.",
  },
  {
    name: "SECURING INDUSTRY mit Tim Bauer (YouTube)",
    url: "https://www.youtube.com/watch?v=fgQ49aQuyp0",
    series: "SECURING INDUSTRY",
    image: "https://img.youtube.com/vi/fgQ49aQuyp0/maxresdefault.jpg",
    videoUrl: "https://www.youtube.com/watch?v=fgQ49aQuyp0",
    embedUrl: "https://www.youtube.com/embed/fgQ49aQuyp0",
    uploadDate: "2025-06-24T09:00:00+02:00",
    description: "SECURING INDUSTRY mit Tim Bauer und David über OT-Security in Industrie und Gebäudeautomation, Normen, Praxis und Betriebsmodelle.",
  },
];


const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Fachsprechpartner Tim Bauer | OT-Security & GA-Fachplanung</title>
        <meta name="description" content="Unabhängige Beratung zu OT-Security, GA-Fachplanung und NIS2 für Gebäudeautomation. BACnet/SC, IEC 62443, OT-Asset-Management. Jetzt Erstgespräch buchen." />
        <link rel="canonical" href="https://fachsprechpartner.de/" />
        <link rel="alternate" hrefLang="de" href="https://fachsprechpartner.de/" />
        <link rel="alternate" hrefLang="x-default" href="https://fachsprechpartner.de/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fachsprechpartner.de/" />
        <meta property="og:title" content="Fachsprechpartner Tim Bauer | OT-Security & GA-Fachplanung" />
        <meta property="og:description" content="Unabhängige Beratung zu OT-Security, GA-Fachplanung und NIS2 in der Gebäudeautomation. BACnet/SC, IEC 62443, OT-Asset-Management." />
        <meta property="og:image" content="https://fachsprechpartner.de/og-image.webp" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        })}</script>
        {services.map((s) => (
          <script key={s.id} type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `https://fachsprechpartner.de/#${s.id}`,
            name: s.name,
            serviceType: s.serviceType,
            description: s.description,
            provider: { "@id": "https://fachsprechpartner.de/#organization" },
            areaServed: ["DE", "AT", "CH"],
            audience: { "@type": "BusinessAudience", audienceType: s.audience },
          })}</script>
        ))}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": "https://fachsprechpartner.de/#podcasts",
          name: "Podcast-Auftritte von Tim Bauer",
          description: "Podcast-Interviews mit Tim Bauer zu OT-Security, Gebäudeautomation, IEC 62443 und Smart Buildings.",
          url: "https://fachsprechpartner.de/#about",
          itemListElement: podcastEpisodes.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "PodcastEpisode",
              "@id": p.url,
              name: p.name,
              url: p.url,
              image: p.image,
              description: p.description,
              partOfSeries: { "@type": "PodcastSeries", name: p.series },
              actor: { "@type": "Person", name: "Tim Bauer", "@id": "https://fachsprechpartner.de/#person" },
              ...(p.videoUrl ? { video: { "@type": "VideoObject", name: p.name, description: p.description, contentUrl: p.videoUrl, embedUrl: p.embedUrl, thumbnailUrl: [p.image], uploadDate: p.uploadDate } } : {}),
              ...(p.audioUrl ? { associatedMedia: { "@type": "AudioObject", contentUrl: p.audioUrl, name: p.name, description: p.description } } : {}),
            },
          })),
        })}</script>
        <meta property="og:video" content="https://www.youtube.com/watch?v=88wgcJg9S6k" />
        <meta property="og:video:type" content="text/html" />
      </Helmet>
      <Navbar />
      <HeroSection />
      <Suspense fallback={null}>
        <AboutSection />
        <ServicesSection />
        <PrimebaseSection />
        <SecnosticSection />
        <SpeakerSection />
        <ContactSection />
      </Suspense>
      <WorkbookFloatingCta />
    </div>
  );
};

export default Index;
